import { __awaiter, __generator, __assign } from 'tslib';
import { addDays, differenceInDays } from 'date-fns';
import { invariant } from '@vp/digital-invariant-lib-ubik';
import { RetryPolicy, GraphqlClientDispenser, HttpClientDispenser } from '@vp/digital-http-client-lib-ubik';
import { getLibraryLoggingAttributes, getLogger, Dispenser } from '@vp/digital-logging-lib-ubik';
import { getClientEnvironment, Environment } from '@vp/digital-environment-lib-ubik';
import axios from 'axios';
import { getPartnerIntegrationOrigin } from '@vp/digital-url-service-lib-ubik';
import { getIdentity, useIdentity } from '@vp/digital-auth-lib-ubik';
import { orderTypeEnum, processOrder } from '@vp/digital-cart-lib-ubik';
import { useEffect } from 'react';
import { makeUseAxios } from 'axios-hooks';
import { useLocale } from '@vp/digital-locale-lib-ubik';

var customerLeadTypeEnum;
(function (customerLeadTypeEnum) {
    customerLeadTypeEnum["None"] = "none";
    customerLeadTypeEnum["CurrentPurchaser"] = "currentPurchaser";
    customerLeadTypeEnum["PreviousDiwyPurchaser"] = "previousDiwyPurchaser";
    customerLeadTypeEnum["Trialer"] = "trialer";
    customerLeadTypeEnum["ExpiredTrialer"] = "expiredTrialer";
    customerLeadTypeEnum["PreviousPurchaserUnpublished"] = "previousPurchaserUnpublished";
    customerLeadTypeEnum["MatchingLead"] = "matchingLead";
    customerLeadTypeEnum["MatchingWixFreemiumLead"] = "matchingWixFreemiumLead";
    customerLeadTypeEnum["MatchingWixSinglePremiumLead"] = "matchingWixSinglePremiumLead";
    customerLeadTypeEnum["MatchingWixMultiplePremiumLead"] = "matchingWixMultiplePremiumLead";
    customerLeadTypeEnum["NonMatchingLead"] = "nonMatchingLead";
    customerLeadTypeEnum["NonMatchingWixLead"] = "nonMatchingWixLead";
    customerLeadTypeEnum["WixSinglePremiumWithDomainSearchVoucher"] = "wixSinglePremiumWithDomainSearchVoucher";
    customerLeadTypeEnum["WixSinglePremiumWithDomainSearchWithoutVoucher"] = "wixSinglePremiumWithDomainSearchWithoutVoucher";
    customerLeadTypeEnum["WixNewUserDomainsPurchase"] = "wixNewUserDomainsPurchase";
    customerLeadTypeEnum["WixExistingPremiumSiteUserDomainPurchase"] = "wixExistingPremiumSiteUserDomainPurchase";
    customerLeadTypeEnum["WixFreeUserSiteUpgradeThroughDomains"] = "wixFreeUserSiteUpgradeThroughDomains";
    customerLeadTypeEnum["WixFreeUserWithOwnDomainSiteUpgradeThroughDomains"] = "wixFreeUserWithOwnDomainSiteUpgradeThroughDomains";
    customerLeadTypeEnum["WixSinglePremiumMonthly"] = "wixSinglePremiumMonthly";
})(customerLeadTypeEnum || (customerLeadTypeEnum = {}));

var global$1 = (typeof global !== "undefined" ? global :
  typeof self !== "undefined" ? self :
  typeof window !== "undefined" ? window : {});

var env = {};

// from https://github.com/kumavis/browser-process-hrtime/blob/master/index.js
var performance = global$1.performance || {};
performance.now        ||
  performance.mozNow     ||
  performance.msNow      ||
  performance.oNow       ||
  performance.webkitNow  ||
  function(){ return (new Date()).getTime() };

var browser$1 = {
  env: env};

var SUBSCRIPTION_LENGTH_DAYS = 30;
function getDaysLeft(createdAt, nowAt) {
    var createdAtDate = new Date(createdAt);
    var expirationDate = addDays(createdAtDate, SUBSCRIPTION_LENGTH_DAYS);
    var daysLeftTrial = nowAt > expirationDate.valueOf()
        ? 0
        : differenceInDays(expirationDate, nowAt);
    var daysSincePurchase = differenceInDays(nowAt, createdAtDate);
    return [daysLeftTrial, daysSincePurchase];
}

var TOWER_TYPE = 'TOWER';
function findTowerAccount(accountGroup) {
    for (var _i = 0, _a = accountGroup.accounts; _i < _a.length; _i++) {
        var account = _a[_i];
        if (account.benefit.benefitType === TOWER_TYPE) {
            return account;
        }
    }
}

var ACTIVE_ACCOUNT = 'ACTIVE';
function findCurrentTowerAccountGroup(accountGroups) {
    if (!accountGroups) {
        return null;
    }
    accountGroups.sort(function (ag1, ag2) {
        return ag2.createdDate.localeCompare(ag1.createdDate);
    });
    var towerAccountGroup = accountGroups.find(function (accountGroup) {
        return accountGroup.accounts.find(function (account) {
            return account.accountType === TOWER_TYPE &&
                account.status === ACTIVE_ACCOUNT;
        });
    });
    return towerAccountGroup;
}

function getTrialType(daysLeftTrial, daysSincePurchase) {
    if (daysLeftTrial > 0) {
        return customerLeadTypeEnum.Trialer;
    }
    else if (daysSincePurchase < 90) {
        return customerLeadTypeEnum.ExpiredTrialer;
    }
    return customerLeadTypeEnum.ExpiredTrialer;
}

var name = "@vp/digital-paid-lib-ubik";
var version = "0.21.3";

var getLibLogger = function (attributes) {
    var attr = getLibraryLoggingAttributes(name, version, attributes);
    return getLogger(attr);
};

var getDefaultConfig = function (config) { return ({
    paidApi: {
        baseUrl: config.paidApiOrigin,
        operations: {
            getAccountGroups: {
                timeoutMS: 5 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
        },
    },
    accountServiceApi: {
        baseUrl: config.accountServiceOrigin,
        operations: {
            createFreeAccountGroup: {
                timeoutMS: 10 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
            getSubscriptionGroupAltId: {
                timeoutMS: 10 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
        },
    },
    partnerServiceApi: {
        baseUrl: config.partnerServiceApiOrigin,
        operations: {
            getPartnerStandardCardAccount: {
                timeoutMS: 5 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
            getPartnerDomainSearchData: {
                timeoutMS: 5 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
            getPartnerAccountDomainSearchData: {
                timeoutMS: 10 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
            getPartnerOrderConfirmationData: {
                timeoutMS: 5 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
            getPartnerDomainRegistrantId: {
                timeoutMS: 10 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
        },
    },
    subscriptionsServiceApi: {
        baseUrl: config.subscriptionsServiceApiOrigin,
        operations: {
            getCurrentPlanInfo: {
                timeoutMS: 5 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
            scheduledChange: {
                timeoutMS: 5 * 1000,
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 10,
                },
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
            },
        },
    },
}); };

var prodConfig = {
    accountServiceOrigin: 'https://account-service.orders.vpsvc.com',
    paidApiOrigin: 'https://subscription-data-api.orders.vpsvc.com',
    partnerServiceApiOrigin: 'https://partners.vpsvc.com',
    subscriptionsServiceApiOrigin: getSubscriptionOrigin(),
};
var stagingConfig = {
    accountServiceOrigin: 'https://account-service-staging.orders.vpsvc.com',
    paidApiOrigin: 'https://subscription-data-api-staging.orders.vpsvc.com',
    partnerServiceApiOrigin: 'https://qa.partner-integration.digital.vpsvc.com',
    subscriptionsServiceApiOrigin: getSubscriptionOrigin(false),
};
var localConfig = {
    accountServiceOrigin: 'https://account-service-staging.orders.vpsvc.com',
    paidApiOrigin: 'https://subscription-data-api-staging.orders.vpsvc.com',
    partnerServiceApiOrigin: 'https://qa.partner-integration.digital.vpsvc.com',
    subscriptionsServiceApiOrigin: getSubscriptionOrigin(false),
};
function getSubscriptionOrigin(isProd) {
    if (isProd === void 0) { isProd = true; }
    if (!isProd) {
        return 'https://subscription-service-staging.orders.vpsvc.com';
    }
    return 'https://subscription-service.orders.vpsvc.com';
}

function getClientConfig() {
    var env = getClientEnvironment();
    switch (env) {
        case Environment.Prod:
            return prodConfig;
        case Environment.Staging:
            return stagingConfig;
        case Environment.Local:
            return localConfig;
    }
    throw new Error('Cannot determine which environment to use');
}

function getAccountGroupsQuery(shopperId, country) {
    return {
        query: "{\n      accountGroups: accountGroupsByShopperId(\n        shopperId: \"".concat(shopperId, "\"\n        country: \"").concat(country, "\"\n        excludeMonolithAccountGroups: false\n      ) {\n        ... on AccountGroup {\n          source\n          altId\n        }\n        ... on MonolithMappedAccountGroup {\n          source\n        }\n      }\n    }"),
    };
}

function getAccountGroupsWithMetadataQuery(shopperId, country) {
    return {
        query: "{\n      accountGroups: accountGroupsByShopperId(\n        shopperId: \"".concat(shopperId, "\"\n        country: \"").concat(country, "\"\n        excludeMonolithAccountGroups: true\n      ) {\n        ... on AccountGroup {\n          source,\n          altId,\n          createdDate,\n          packageAltId,\n          isTrial\n          accounts {\n            altId,\n            accountType,\n            status,\n            benefitAltId,\n            subscription {\n              productId\n            },\n            benefit {\n              ... on Benefit {\n              altId,\n              createdDate,\n              digitalUserId,\n              benefitGroupAltId,\n              benefitType,\n              locale,\n              status,\n              modifierFlags,\n              properties,\n              entitlements,\n              externalId,\n              external {\n                  ... on TowerSite {\n                      siteId,\n                      createdAt,\n                      updatedAt,\n                      shopperId,\n                      benefitId,\n                      legacy,\n                      version,\n                      editVersion,\n                      firstPublishedAt,\n                      lastPublishedAt,\n                      domain,\n                      domains,\n                      isFrozen,\n                      isCancelled,\n                      isUnpublished\n                      sitePreviewUrl,\n                      templatePreviewUrl,\n                      }\n                  }\n              }\n            }\n          }\n        }\n      }\n  }"),
    };
}

var config$c = getClientConfig();
var apiConfig$c = getDefaultConfig(config$c).paidApi;
var opConfig$c = apiConfig$c.operations.getAccountGroups;
var ddapiClientDispenser$1 = new GraphqlClientDispenser(apiConfig$c, opConfig$c, true);
function getAccountGroups(shopperId, country, includeMetadata) {
    return __awaiter(this, void 0, void 0, function () {
        var query, axiosConfig, accountGroups;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(shopperId, 'Required parameter is missing: shopperId');
                    invariant(country, 'Required parameter is missing: country');
                    query = getAccountGroupsWithMetadataQuery(shopperId, country)
                        ;
                    axiosConfig = {
                        url: '/api/v1',
                        data: query,
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, ddapiClientDispenser$1
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    accountGroups = (_a.sent()).data;
                    return [2, accountGroups];
                case 3:
                    _a.sent();
                    getLibLogger().warn('getAccountGroups: Request to get accountGroups failed', { shopperId: shopperId });
                    return [2, { accountGroups: [] }];
                case 4: return [2];
            }
        });
    });
}

var config$b = getClientConfig();
var apiConfig$b = getDefaultConfig(config$b).partnerServiceApi;
var opConfig$b = apiConfig$b.operations.getPartnerStandardCardAccount;
new HttpClientDispenser(apiConfig$b, opConfig$b, true);
function getPartnerAccountGroup(shopperId, locale) {
    return __awaiter(this, void 0, void 0, function () {
        var accessToken, pathAndQueryString, PARTNER_ORIGIN, partnerStandardCardAccount;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(shopperId, 'Required parameter is missing: shopperId');
                    invariant(locale, 'Required parameter is missing: locale');
                    accessToken = getIdentity().accessToken;
                    pathAndQueryString = "api/v1/project/card/workspace/WIX/".concat(shopperId, "/").concat(locale);
                    PARTNER_ORIGIN = getPartnerIntegrationOrigin();
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, axios.get("".concat(PARTNER_ORIGIN, "/").concat(pathAndQueryString), {
                            headers: {
                                'Content-Type': 'application/json',
                                authorization: "Bearer ".concat(accessToken),
                            },
                        })];
                case 2:
                    partnerStandardCardAccount = (_a.sent()).data;
                    return [2, partnerStandardCardAccount];
                case 3:
                    _a.sent();
                    getLibLogger().warn('getPartnerAccountGroups: Request to get partnerAccountGroups failed', { shopperId: shopperId });
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

function getPartnerOrderDataWithSiteId(shopperId, locale, siteIdsList) {
    return __awaiter(this, void 0, void 0, function () {
        var accessToken, pathAndQueryString, PARTNER_ORIGIN, partnerOrderData;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(shopperId, 'Required parameter is missing: shopperId');
                    invariant(locale, 'Required parameter is missing: locale');
                    invariant(siteIdsList, 'Required parameter is missing: siteIds');
                    accessToken = getIdentity().accessToken;
                    pathAndQueryString = "api/v1/project/card/orderConfirmation/WIX/".concat(shopperId, "/").concat(locale);
                    PARTNER_ORIGIN = getPartnerIntegrationOrigin();
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, axios.get("".concat(PARTNER_ORIGIN, "/").concat(pathAndQueryString), {
                            headers: {
                                'Content-Type': 'application/json',
                                authorization: "Bearer ".concat(accessToken),
                                siteId: siteIdsList,
                            },
                        })];
                case 2:
                    partnerOrderData = (_a.sent()).data;
                    return [2, partnerOrderData];
                case 3:
                    _a.sent();
                    getLibLogger().warn('getPartnerOrderDataWithSiteId: Request to get getPartnerOrderDataWithSiteId failed', { shopperId: shopperId });
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

function getPartnerAccountProducts(shopperId) {
    return __awaiter(this, void 0, void 0, function () {
        var logger, accessToken, pathAndQueryString, PARTNER_ORIGIN, partnerStandardCardAccount, e_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(shopperId, 'Required parameter is missing: shopperId');
                    logger = getLibLogger();
                    accessToken = getIdentity().accessToken;
                    pathAndQueryString = "api/v1/products/".concat(shopperId);
                    PARTNER_ORIGIN = getPartnerIntegrationOrigin();
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    logger.info('getPartnerAccountProducts: calling with parameters ', {
                        shopperId: shopperId,
                    });
                    return [4, axios.get("".concat(PARTNER_ORIGIN, "/").concat(pathAndQueryString), {
                            headers: {
                                'Content-Type': 'application/json',
                                authorization: "Bearer ".concat(accessToken),
                            },
                        })];
                case 2:
                    partnerStandardCardAccount = (_a.sent()).data;
                    return [2, partnerStandardCardAccount];
                case 3:
                    e_1 = _a.sent();
                    logger.error('getPartnerAccountProducts: Request to get getPartnerAccountProducts failed', { shopperId: shopperId, e: e_1 });
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

var NO_TRIAL_DAYS = -1;
var isProduction = browser$1.env.NODE_ENV === 'production';
var CONNECT_DOMAINS_PKG_ALT_ID = '761d6fe5-c3c9-41f8-887e-e436de501fd0';
var handleApiError = function (error, message) {
    console.error(message, error);
    if (isProduction) {
        throw new Error(message);
    }
    return null;
};
var fetchAccountAndPartnerData = function (shopperId, locale, country) { return __awaiter(void 0, void 0, void 0, function () {
    var _a, accountGroupData, partnerAccountData, err_1;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                _b.trys.push([0, 2, , 3]);
                return [4, Promise.all([
                        getAccountGroups(shopperId, country),
                        getPartnerAccountGroup(shopperId, locale),
                    ])];
            case 1:
                _a = _b.sent(), accountGroupData = _a[0], partnerAccountData = _a[1];
                return [2, { accountGroupData: accountGroupData, partnerAccountData: partnerAccountData }];
            case 2:
                err_1 = _b.sent();
                return [2, handleApiError(err_1, 'Failed to fetch account or partner data')];
            case 3: return [2];
        }
    });
}); };
function getCustomerLeadData(shopperId_1, locale_1) {
    return __awaiter(this, arguments, void 0, function (shopperId, locale, isWixLaunched, sitesIds, wixDomains, wixPlusSiteIds) {
        var country, partnerOrderData, _a, accountGroupData, partnerAccountData, towerAccountGroup, customerProductDetails, existingPremiumSiteUser, newUserDomainData, numberOfSitesOrdered, towerAccount, towerSite, _b, daysLeftTrial, daysSincePurchase, custType;
        var _c, _d, _e, _f, _g;
        if (isWixLaunched === void 0) { isWixLaunched = false; }
        return __generator(this, function (_h) {
            switch (_h.label) {
                case 0:
                    country = locale.length > 2 ? locale.substring(3, 4) : locale;
                    if (wixPlusSiteIds && wixPlusSiteIds.length > 0) {
                        return [2, [customerLeadTypeEnum.PreviousDiwyPurchaser, NO_TRIAL_DAYS, {}]];
                    }
                    return [4, fetchAccountAndPartnerData(shopperId, locale, country)];
                case 1:
                    _a = _h.sent(), accountGroupData = _a.accountGroupData, partnerAccountData = _a.partnerAccountData;
                    if (!accountGroupData) {
                        return [2, [customerLeadTypeEnum.NonMatchingLead, NO_TRIAL_DAYS, {}]];
                    }
                    towerAccountGroup = findCurrentTowerAccountGroup(accountGroupData.accountGroups);
                    if (!isWixLaunched) return [3, 6];
                    if (!partnerAccountData) {
                        return [2, towerAccountGroup
                                ? null
                                : [customerLeadTypeEnum.NonMatchingWixLead, NO_TRIAL_DAYS, {}]];
                    }
                    if (!(sitesIds && sitesIds.length > 0)) return [3, 3];
                    return [4, getPartnerOrderDataWithSiteId(shopperId, locale, sitesIds).catch(function (err) {
                            var msg = "getPartnerOrderDataWithSiteId: Request to get partner data failed ".concat(err);
                            return handleApiError(err, msg);
                        })];
                case 2:
                    partnerOrderData = _h.sent();
                    _h.label = 3;
                case 3:
                    if (!(!sitesIds && wixDomains && wixDomains.length > 0)) return [3, 5];
                    return [4, getPartnerAccountProducts(shopperId)];
                case 4:
                    customerProductDetails = _h.sent();
                    existingPremiumSiteUser = (_c = customerProductDetails === null || customerProductDetails === void 0 ? void 0 : customerProductDetails.wixSites) === null || _c === void 0 ? void 0 : _c.find(function (site) { return site.premium; });
                    if (existingPremiumSiteUser) {
                        return [2, [
                                customerLeadTypeEnum.WixExistingPremiumSiteUserDomainPurchase,
                                NO_TRIAL_DAYS,
                                existingPremiumSiteUser,
                            ]];
                    }
                    newUserDomainData = (_d = partnerAccountData === null || partnerAccountData === void 0 ? void 0 : partnerAccountData.standardCards) === null || _d === void 0 ? void 0 : _d.find(function (project) { return project.projectId === wixDomains[0]; });
                    return [2, [
                            customerLeadTypeEnum.WixNewUserDomainsPurchase,
                            NO_TRIAL_DAYS,
                            newUserDomainData,
                        ]];
                case 5:
                    numberOfSitesOrdered = (_f = (_e = partnerOrderData === null || partnerOrderData === void 0 ? void 0 : partnerOrderData.orderConfirmationCard) === null || _e === void 0 ? void 0 : _e.siteCards) === null || _f === void 0 ? void 0 : _f.length;
                    if (numberOfSitesOrdered === 1) {
                        if (((_g = partnerOrderData === null || partnerOrderData === void 0 ? void 0 : partnerOrderData.orderConfirmationCard) === null || _g === void 0 ? void 0 : _g.siteCards[0].packageAltID) !=
                            CONNECT_DOMAINS_PKG_ALT_ID) {
                            return [2, [
                                    customerLeadTypeEnum.WixSinglePremiumWithDomainSearchVoucher,
                                    NO_TRIAL_DAYS,
                                    partnerOrderData,
                                ]];
                        }
                        return [2, [
                                customerLeadTypeEnum.WixSinglePremiumWithDomainSearchWithoutVoucher,
                                NO_TRIAL_DAYS,
                                partnerOrderData,
                            ]];
                    }
                    else if (numberOfSitesOrdered > 1) {
                        return [2, [
                                customerLeadTypeEnum.MatchingWixMultiplePremiumLead,
                                NO_TRIAL_DAYS,
                                partnerOrderData,
                            ]];
                    }
                    else {
                        return [2, null];
                    }
                case 6:
                    if (!accountGroupData) {
                        return [2, [customerLeadTypeEnum.NonMatchingLead, NO_TRIAL_DAYS, {}]];
                    }
                    if (!towerAccountGroup) {
                        return [2, [customerLeadTypeEnum.NonMatchingLead, -1, {}]];
                    }
                    towerAccount = findTowerAccount(towerAccountGroup);
                    towerSite = towerAccount.benefit.external;
                    if (towerAccount.subscription && !towerSite.firstPublishedAt) {
                        return [2, [
                                customerLeadTypeEnum.PreviousPurchaserUnpublished,
                                NO_TRIAL_DAYS,
                                towerAccount,
                            ]];
                    }
                    if (towerAccount.subscription && towerSite.firstPublishedAt) {
                        return [2, [customerLeadTypeEnum.None, NO_TRIAL_DAYS, towerAccount]];
                    }
                    if (!towerAccountGroup.isTrial) {
                        return [2, [customerLeadTypeEnum.None, NO_TRIAL_DAYS, towerAccount]];
                    }
                    if (towerSite.firstPublishedAt) {
                        return [2, [customerLeadTypeEnum.None, NO_TRIAL_DAYS, towerAccount]];
                    }
                    _b = getDaysLeft(towerSite.createdAt, Date.now()), daysLeftTrial = _b[0], daysSincePurchase = _b[1];
                    custType = getTrialType(daysLeftTrial, daysSincePurchase);
                    return [2, [custType, daysLeftTrial, towerAccount]];
                case 7: return [2];
            }
        });
    });
}

function processCustomer(order, shopperId, country, accessToken) {
    return __awaiter(this, void 0, void 0, function () {
        var _a, towerProduct, cartTemplate, customerLeadData;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4, processOrder(order, shopperId)];
                case 1:
                    _a = _b.sent(), towerProduct = _a[0], cartTemplate = _a[1];
                    if (towerProduct === orderTypeEnum.TowerDiy ||
                        towerProduct === orderTypeEnum.TowerDify ||
                        towerProduct === orderTypeEnum.TowerDifyRecreation) {
                        return [2, [customerLeadTypeEnum.CurrentPurchaser, NO_TRIAL_DAYS, cartTemplate]];
                    }
                    return [4, getCustomerLeadData(shopperId, country)];
                case 2:
                    customerLeadData = _b.sent();
                    return [2, customerLeadData];
            }
        });
    });
}

var accountServiceLogger = getLibLogger();
var config$a = getClientConfig();
var apiConfig$a = getDefaultConfig(config$a).accountServiceApi;
var opConfig$a = apiConfig$a.operations.createFreeAccountGroup;
var accountServiceClientDispenser = new HttpClientDispenser(apiConfig$a, opConfig$a, true);
var CREATE_FREE_ACCOUNT_GROUP_PATH = '/api/v2/accountGroup/free';
function createFreeAccountGroup(parameters) {
    return __awaiter(this, void 0, void 0, function () {
        var axiosConfig, accountGroup, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    accountServiceLogger.info('createFreeAccountGroup: calling with parameters ', parameters);
                    axiosConfig = {
                        url: CREATE_FREE_ACCOUNT_GROUP_PATH,
                        method: 'POST',
                        data: parameters,
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, accountServiceClientDispenser
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    accountGroup = (_a.sent()).data;
                    accountServiceLogger.info('createFreeAccountGroup: successfully created free accountGroup', accountGroup);
                    return [2, accountGroup];
                case 3:
                    err_1 = _a.sent();
                    accountServiceLogger.error('createFreeAccountGroup: returned an error', __assign({ response: err_1.response, err: err_1 }, parameters));
                    throw err_1;
                case 4: return [2];
            }
        });
    });
}

var DEFAULT_TOWER_TRY_FIRST_PACKAGE_ALT_ID = 'bbed97ec-bed4-4aa9-9a02-9a7fe70be57f';
var CANCELED = 'CANCELED';
function getOrCreateFreeTowerAccountGroup(parameters) {
    return __awaiter(this, void 0, void 0, function () {
        var shopperId, locale, country, accountGroups, trialAccountGroup, newTrialAccountGroup;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    shopperId = parameters.shopperId, locale = parameters.locale;
                    country = locale.split('_')[1];
                    return [4, getAccountGroups(shopperId, country)];
                case 1:
                    accountGroups = (_a.sent()).accountGroups;
                    trialAccountGroup = accountGroups
                        .filter(function (accountGroup) {
                        return !accountGroup.accounts.every(function (account) { return account.status === CANCELED; });
                    })
                        .sort(function (a, b) { return b.id - a.id; })
                        .find(function (elem) {
                        return elem.packageAltId === DEFAULT_TOWER_TRY_FIRST_PACKAGE_ALT_ID;
                    });
                    if (trialAccountGroup) {
                        return [2, trialAccountGroup];
                    }
                    return [4, createFreeAccountGroup(parameters)];
                case 2:
                    newTrialAccountGroup = _a.sent();
                    return [2, newTrialAccountGroup];
            }
        });
    });
}

var config$9 = getClientConfig();
var apiConfig$9 = getDefaultConfig(config$9).paidApi;
var opConfig$9 = apiConfig$9.operations.getAccountGroups;
var ddapiClientDispenser = new GraphqlClientDispenser(apiConfig$9, opConfig$9, true);
var ddapiHookDispenser = new Dispenser(function () {
    return makeUseAxios({
        axios: ddapiClientDispenser.getInstance(),
        defaultOptions: { manual: true },
    });
});
function useAccountGroups() {
    var _a = ddapiHookDispenser.getInstance()({}), responseValues = _a[0], getAccountGroups = _a[1];
    var countryCode = useLocale().countryCode;
    var _b = useIdentity(), canonicalId = _b.canonicalId, isSignedIn = _b.isSignedIn;
    useEffect(function () {
        if (isSignedIn) {
            var query = getAccountGroupsQuery(canonicalId, countryCode);
            var axiosConfig = {
                url: '/api/v1',
                data: query,
            };
            getAccountGroups(axiosConfig);
        }
    }, [countryCode, getAccountGroups, isSignedIn, canonicalId]);
    return responseValues;
}

var config$8 = getClientConfig();
var apiConfig$8 = getDefaultConfig(config$8).subscriptionsServiceApi;
var opConfig$8 = apiConfig$8.operations.getCurrentPlanInfo;
var subscriptionApiClientDispenser$3 = new HttpClientDispenser(apiConfig$8, opConfig$8, true);
function getSubscriptionGroupData(subscriptionGroupAltId) {
    return __awaiter(this, void 0, void 0, function () {
        var axiosConfig, data, e_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(subscriptionGroupAltId, 'Required parameter is missing: subscriptionGroupAltId');
                    axiosConfig = {
                        url: "/api/v2/subscriptionGroup/".concat(subscriptionGroupAltId),
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, subscriptionApiClientDispenser$3
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    data = _a.sent();
                    return [2, data.data];
                case 3:
                    e_1 = _a.sent();
                    getLibLogger().error('getSubscriptionInfos: Request to get partnerAccountGroups failed', { subscriptionGroupAltId: subscriptionGroupAltId, e: e_1 });
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

function getSubscriptionData(subscriptionGroupAltId) {
    return __awaiter(this, void 0, void 0, function () {
        var currentPlanData, subscriptionGroupData, latestSubscription;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(subscriptionGroupAltId, 'Required parameter is missing: subscriptionGroupAltId');
                    currentPlanData = {
                        currentPlan: '',
                        nextBillDate: '',
                        diffDate: 0,
                    };
                    return [4, getSubscriptionGroupData(subscriptionGroupAltId)];
                case 1:
                    subscriptionGroupData = _a.sent();
                    if (!subscriptionGroupData ||
                        subscriptionGroupData.subscriptionGroupStatus !== 'ACTIVE') {
                        return [2, null];
                    }
                    currentPlanData.nextBillDate = subscriptionGroupData.nextBillDate;
                    currentPlanData.diffDate = differenceInDays(new Date().getTime(), new Date(subscriptionGroupData.creationDate).getTime());
                    latestSubscription = subscriptionGroupData === null || subscriptionGroupData === void 0 ? void 0 : subscriptionGroupData.subscriptions.find(function (subscription) { return subscription.subscriptionStatus === 'ACTIVE'; });
                    if (!latestSubscription) {
                        return [2, null];
                    }
                    currentPlanData.currentPlan =
                        latestSubscription.productAttributes.packageLevel ||
                            latestSubscription.productAttributes['Package Level'];
                    return [2, currentPlanData];
            }
        });
    });
}

var config$7 = getClientConfig();
var apiConfig$7 = getDefaultConfig(config$7).partnerServiceApi;
var opConfig$7 = apiConfig$7.operations.getPartnerStandardCardAccount;
var partnerApiClientDispenser$2 = new HttpClientDispenser(apiConfig$7, opConfig$7, true);
var partnerHookDispenser$2 = new Dispenser(function () {
    return makeUseAxios({
        axios: partnerApiClientDispenser$2.getInstance(),
        defaultOptions: { manual: true },
    });
});
function usePartnerAccountGroups() {
    var _a = partnerHookDispenser$2.getInstance()({}), responseValues = _a[0], getPartnerStandardCardAccount = _a[1];
    var _b = useIdentity(), shopperId = _b.canonicalId, isSignedIn = _b.isSignedIn;
    var locale = useLocale().identifier;
    useEffect(function () {
        if (isSignedIn) {
            var axiosConfig = {
                url: "api/v1/project/card/workspace/WIX/".concat(shopperId, "/").concat(locale),
            };
            getPartnerStandardCardAccount(axiosConfig);
        }
    }, [getPartnerStandardCardAccount, isSignedIn, shopperId, locale]);
    return responseValues;
}

var config$6 = getClientConfig();
var apiConfig$6 = getDefaultConfig(config$6).partnerServiceApi;
var opConfig$6 = apiConfig$6.operations.getPartnerDomainSearchData;
var partnerApiClientDispenser$1 = new HttpClientDispenser(apiConfig$6, opConfig$6);
var partnerHookDispenser$1 = new Dispenser(function () {
    return makeUseAxios({
        axios: partnerApiClientDispenser$1.getInstance(),
        defaultOptions: { manual: true },
    });
});
function usePartnerDomainSearch(domainName, locale, source, requestor) {
    invariant(domainName, 'Required parameter is missing: domainName');
    invariant(locale, 'Required parameter is missing: locale');
    var _a = partnerHookDispenser$1.getInstance()({}), responseValues = _a[0], getPartnerDomainSearchData = _a[1];
    useEffect(function () {
        var axiosConfig = {
            url: "/api/anonymous/v1/domain/search",
            method: 'POST',
            data: { domain: domainName, source: source, requestor: requestor },
            params: { locale: locale },
        };
        getPartnerDomainSearchData(axiosConfig);
    }, [domainName, getPartnerDomainSearchData]);
    return responseValues;
}

var config$5 = getClientConfig();
var apiConfig$5 = getDefaultConfig(config$5).partnerServiceApi;
var opConfig$5 = apiConfig$5.operations.getPartnerStandardCardAccount;
var partnerApiClientDispenser = new HttpClientDispenser(apiConfig$5, opConfig$5, true);
var partnerHookDispenser = new Dispenser(function () {
    return makeUseAxios({
        axios: partnerApiClientDispenser.getInstance(),
        defaultOptions: { manual: true },
    });
});
function usePartnerAccountProducts() {
    var _a = partnerHookDispenser.getInstance()({}), responseValues = _a[0], getPartnerAccountProducts = _a[1];
    var _b = useIdentity(), shopperId = _b.canonicalId, isSignedIn = _b.isSignedIn;
    useEffect(function () {
        if (isSignedIn) {
            var axiosConfig = {
                url: "api/v1/products/".concat(shopperId),
            };
            getPartnerAccountProducts(axiosConfig);
        }
    }, [getPartnerAccountProducts, isSignedIn, shopperId]);
    return responseValues;
}

function getPartnerAccountData(shopperId) {
    return __awaiter(this, void 0, void 0, function () {
        var accessToken, pathAndQueryString, PARTNER_ORIGIN, partnerAccountData;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(shopperId, 'Required parameter is missing: shopperId');
                    accessToken = getIdentity().accessToken;
                    pathAndQueryString = "api/v1/account/WIX/".concat(shopperId);
                    PARTNER_ORIGIN = getPartnerIntegrationOrigin();
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, axios.get("".concat(PARTNER_ORIGIN, "/").concat(pathAndQueryString), {
                            headers: {
                                'Content-Type': 'application/json',
                                authorization: "Bearer ".concat(accessToken),
                            },
                        })];
                case 2:
                    partnerAccountData = (_a.sent()).data;
                    return [2, partnerAccountData];
                case 3:
                    _a.sent();
                    getLibLogger().warn('getPartnerAccountData: Request to get getPartnerAccountData failed', { shopperId: shopperId });
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

var logger$1 = getLibLogger();
var config$4 = getClientConfig();
var apiConfig$4 = getDefaultConfig(config$4).partnerServiceApi;
var opConfig$4 = apiConfig$4.operations.getPartnerAccountDomainSearchData;
var partnerServiceApiClientDispenser$1 = new HttpClientDispenser(apiConfig$4, opConfig$4, true);
function getPartnerAccountDomainSearch(domainName_1, shopperId_1, locale_1, source_1) {
    return __awaiter(this, arguments, void 0, function (domainName, shopperId, locale, source, requestor) {
        var axiosConfig, data, error_1, partnerAccountDomainSearchData;
        var _a, _b;
        if (requestor === void 0) { requestor = 'Digital'; }
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    invariant(domainName, 'Required parameter is missing: domainName');
                    invariant(shopperId, 'Required parameter is missing: shopperId');
                    invariant(locale, 'Required parameter is missing: locale');
                    source = source || '';
                    axiosConfig = {
                        url: "/api/v1/domain/search/WIX/".concat(shopperId),
                        method: 'POST',
                        data: { domain: domainName },
                        params: { locale: locale, source: source, requestor: requestor },
                    };
                    logger$1.info('getPartnerAccountDomainSearch: calling with parameters ', {
                        domain: domainName,
                        locale: locale,
                        shopperId: shopperId,
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4, partnerServiceApiClientDispenser$1
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    data = (_c.sent()).data;
                    return [2, data];
                case 3:
                    error_1 = _c.sent();
                    logger$1.warn('getPartnerAccountDomainSearch: Request to get PartnerAccountDomainSearchResponse failed', { domain: domainName, error: error_1 });
                    partnerAccountDomainSearchData = (_b = (_a = error_1 === null || error_1 === void 0 ? void 0 : error_1.response) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : {
                        domain: domainName,
                        available: false,
                        suggestions: [],
                        errorResponse: {
                            message: null,
                            details: null,
                        },
                    };
                    return [2, partnerAccountDomainSearchData];
                case 4: return [2];
            }
        });
    });
}

var logger = getLibLogger();
function getPartnerDomainSearch(domainName_1, locale_1, source_1) {
    return __awaiter(this, arguments, void 0, function (domainName, locale, source, requestor) {
        var PARTNER_ORIGIN, data, error_1, partnerAccountDomainSearchData;
        var _a, _b;
        if (requestor === void 0) { requestor = 'Digital'; }
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    invariant(domainName, 'Required parameter is missing: domainName');
                    invariant(locale, 'Required parameter is missing: locale');
                    logger.info('getPartnerDomainSearch: calling with parameters ', {
                        domain: domainName,
                        locale: locale,
                    });
                    PARTNER_ORIGIN = getPartnerIntegrationOrigin();
                    source = source || '';
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4, axios.post("".concat(PARTNER_ORIGIN, "/api/anonymous/v1/domain/search"), {
                            domain: domainName,
                        }, {
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            params: { locale: locale, source: source, requestor: requestor },
                        })];
                case 2:
                    data = (_c.sent()).data;
                    return [2, data];
                case 3:
                    error_1 = _c.sent();
                    logger.error('getPartnerDomainSearch: Request to get PartnerDomainSearchResponse failed', { domain: domainName, error: error_1 });
                    partnerAccountDomainSearchData = (_b = (_a = error_1 === null || error_1 === void 0 ? void 0 : error_1.response) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : {
                        domain: domainName,
                        available: false,
                        suggestions: [],
                        errorResponse: {
                            message: null,
                            details: null,
                        },
                    };
                    return [2, partnerAccountDomainSearchData];
                case 4: return [2];
            }
        });
    });
}

var loggerService = getLibLogger();
var config$3 = getClientConfig();
var DOMAIN_REGISTRATION_PATH = '/api/v1/domain/register/WIX/';
var apiConfig$3 = getDefaultConfig(config$3).partnerServiceApi;
var opConfig$3 = apiConfig$3.operations.getPartnerDomainRegistrantId;
var partnerServiceApiClientDispenser = new HttpClientDispenser(apiConfig$3, opConfig$3, true);
function getPartnerDomainRegistrantId(cannonicalID, locale, siteId, data) {
    return __awaiter(this, void 0, void 0, function () {
        var axiosConfig, partnerDomainRegistrantId, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(cannonicalID, 'Required parameter is missing: cannonicalID');
                    invariant(locale, 'Required parameter is missing: locale');
                    invariant(data, 'Required parameter is missing: data');
                    loggerService.info('getPartnerDomainRegistrantId: calling with parameters ', data);
                    axiosConfig = {
                        url: "".concat(DOMAIN_REGISTRATION_PATH).concat(cannonicalID),
                        method: 'POST',
                        data: data,
                        params: { locale: locale, siteId: siteId },
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, partnerServiceApiClientDispenser
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    partnerDomainRegistrantId = (_a.sent()).data;
                    return [2, partnerDomainRegistrantId];
                case 3:
                    err_1 = _a.sent();
                    loggerService.warn('getPartnerDomainRegistrantId: Request to get partnerAccountDomainRegistrationResponse failed', { cannonicalID: cannonicalID, err: err_1 });
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

var config$2 = getClientConfig();
var apiConfig$2 = getDefaultConfig(config$2).subscriptionsServiceApi;
var opConfig$2 = apiConfig$2.operations.getCurrentPlanInfo;
var subscriptionApiClientDispenser$2 = new HttpClientDispenser(apiConfig$2, opConfig$2, true);
function getUnconsumedAmountSubscriptionData(subscriptionGroupAltId) {
    return __awaiter(this, void 0, void 0, function () {
        var axiosConfig, data, e_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    invariant(subscriptionGroupAltId, 'Required parameter is missing: subscriptionGroupAltId');
                    axiosConfig = {
                        url: "/api/v2/subscriptionGroup/".concat(subscriptionGroupAltId, "/unconsumed"),
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, subscriptionApiClientDispenser$2
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    data = _a.sent();
                    return [2, data.data];
                case 3:
                    e_1 = _a.sent();
                    getLibLogger().error('getUnconsumedAmountSubscriptionData: Request to get partnerAccountGroups failed', { subscriptionGroupAltId: subscriptionGroupAltId, e: e_1 });
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

var config$1 = getClientConfig();
var apiConfig$1 = getDefaultConfig(config$1).subscriptionsServiceApi;
var opConfig$1 = apiConfig$1.operations.scheduledChange;
var subscriptionApiClientDispenser$1 = new HttpClientDispenser(apiConfig$1, opConfig$1, true);
function scheduleSubscriptionChange(changeType, billingInterval, intervalFrequency, subscriptions, subscriptionGroupAltId) {
    return __awaiter(this, void 0, void 0, function () {
        var requestBody, axiosConfig, response, e_1, errorDetails;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    invariant(changeType, 'Required parameter is missing: changeType');
                    invariant(billingInterval, 'Required parameter is missing: billingInterval');
                    invariant(intervalFrequency, 'Required parameter is missing: intervalFrequency');
                    invariant(subscriptions, 'Required parameter is missing: subscriptions');
                    invariant(subscriptionGroupAltId, 'Required parameter is missing: subscriptionGroupAltId');
                    requestBody = {
                        changeType: changeType,
                        executionData: {
                            billingInterval: billingInterval,
                            intervalFrequency: intervalFrequency,
                            subscriptions: subscriptions,
                        },
                        subscriptionGroupAltId: subscriptionGroupAltId,
                    };
                    axiosConfig = {
                        method: 'POST',
                        url: '/api/v2/scheduledChange',
                        data: requestBody,
                    };
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4, subscriptionApiClientDispenser$1
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    response = _b.sent();
                    return [2, response.data];
                case 3:
                    e_1 = _b.sent();
                    errorDetails = {
                        changeType: changeType,
                        billingInterval: billingInterval,
                        intervalFrequency: intervalFrequency,
                        subscriptions: subscriptions,
                        subscriptionGroupAltId: subscriptionGroupAltId,
                        error: e_1.message,
                        stack: e_1.stack,
                        response: ((_a = e_1.response) === null || _a === void 0 ? void 0 : _a.data) || null,
                    };
                    getLibLogger().error('scheduleSubscriptionChange: Request to schedule subscription change failed', errorDetails);
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

var config = getClientConfig();
var apiConfig = getDefaultConfig(config).subscriptionsServiceApi;
var opConfig = apiConfig.operations.getCurrentPlanInfo;
var subscriptionApiClientDispenser = new HttpClientDispenser(apiConfig, opConfig, true);
function getSubscriptionGroupByShopperId(shopperId) {
    return __awaiter(this, void 0, void 0, function () {
        var axiosConfig, response, e_1;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    invariant(shopperId, 'Required parameter is missing: shopperId');
                    axiosConfig = {
                        url: "/api/v2/subscriptionGroup",
                        method: 'GET',
                        params: { customerId: shopperId },
                    };
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4, subscriptionApiClientDispenser
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    response = _c.sent();
                    return [2, (_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.content) !== null && _b !== void 0 ? _b : null];
                case 3:
                    e_1 = _c.sent();
                    getLibLogger().error('getSubscriptionGroupByShopperId: Failed to fetch subscription groups by shopperId', { shopperId: shopperId, e: e_1 });
                    return [2, null];
                case 4: return [2];
            }
        });
    });
}

export { DEFAULT_TOWER_TRY_FIRST_PACKAGE_ALT_ID, customerLeadTypeEnum, getCustomerLeadData, getOrCreateFreeTowerAccountGroup, getPartnerAccountData, getPartnerAccountDomainSearch, getPartnerAccountGroup, getPartnerAccountProducts, getPartnerDomainRegistrantId, getPartnerDomainSearch, getPartnerOrderDataWithSiteId, getSubscriptionData, getSubscriptionGroupByShopperId, getSubscriptionGroupData, getUnconsumedAmountSubscriptionData, processCustomer, scheduleSubscriptionChange, useAccountGroups, usePartnerAccountGroups, usePartnerAccountProducts, usePartnerDomainSearch };
